import React, { useRef, useState } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { AlertTriangle, Clock, Info, Trash2 } from "lucide-react";

const SEVERITY_STYLES = {
  high: { ring: "border-[#E07A5F]", chip: "bg-[#FBEAE5] text-[#B04A2A]", icon: AlertTriangle },
  medium: { ring: "border-[#E2C76F]", chip: "bg-[#FAF1D7] text-[#8A6A1A]", icon: Clock },
  low: { ring: "border-[#DCEDF0]", chip: "bg-[#DCEDF0] text-[#1F5566]", icon: Info },
};

// Swipe left this many px to reveal the red delete button.
const REVEAL_WIDTH = 84;
// Swipe past this point and we'll snap to the revealed state (instead of back).
const REVEAL_SNAP_THRESHOLD = REVEAL_WIDTH * 0.45;

/**
 * SwipeableAlert — iOS-Mail-style two-step dismiss.
 *
 * Drag the card left to reveal a red "Delete" button; tap the button to
 * actually dismiss. Tapping anywhere else (or releasing past the snap-back
 * threshold) returns the card to rest. Keyboard / non-touch users get the
 * Delete button via a small `Trash2` icon visible on hover.
 *
 * Wraps the original alert row content via the `children` prop so the
 * SEVERITY styling stays in one place.
 */
export const SwipeableAlert = ({ alert, clickable, onOpen, onDelete }) => {
  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.low;
  const Icon = style.icon;
  const controls = useAnimation();
  const [revealed, setRevealed] = useState(false);
  // Tracks whether the most recent pointer interaction was a drag past the
  // 5px threshold. Used to suppress the row's onClick navigation that would
  // otherwise fire at the END of a desktop mouse drag (framer-motion's drag
  // gesture doesn't stop the underlying click event on its own).
  const wasDraggingRef = useRef(false);

  const snapClosed = () => {
    controls.start({ x: 0, transition: { type: "spring", stiffness: 380, damping: 32 } });
    setRevealed(false);
  };

  const snapOpen = () => {
    controls.start({ x: -REVEAL_WIDTH, transition: { type: "spring", stiffness: 380, damping: 32 } });
    setRevealed(true);
  };

  const handleDragStart = () => {
    wasDraggingRef.current = true;
  };

  const handleDragEnd = (_e, info) => {
    if (info.offset.x < -REVEAL_SNAP_THRESHOLD) {
      snapOpen();
    } else {
      snapClosed();
    }
    // Clear the drag flag on the next tick so the synthetic click that
    // immediately follows the pointer-up sees `wasDragging=true` and bails.
    setTimeout(() => { wasDraggingRef.current = false; }, 0);
  };

  const handleRowClick = () => {
    // Suppress the click that follows a drag — desktop mouse + framer-motion
    // dispatches a click on pointer-up even though the user clearly dragged.
    if (wasDraggingRef.current) return;
    if (revealed) {
      snapClosed();
      return;
    }
    if (clickable) onOpen?.();
  };

  return (
    <li className="relative overflow-hidden rounded-2xl" data-testid={`alert-row-${alert.id}`}>
      {/* Red delete pad — sits behind the card. Tappable once revealed. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.();
        }}
        aria-label={`Delete alert: ${alert.title}`}
        data-testid={`alert-delete-${alert.id}`}
        tabIndex={revealed ? 0 : -1}
        className="absolute inset-y-0 right-0 flex items-center justify-center gap-2 bg-[#E07A5F] hover:bg-[#C9603E] text-white font-semibold text-sm"
        style={{ width: REVEAL_WIDTH }}
      >
        <Trash2 className="w-4 h-4" /> Delete
      </button>

      {/* The card itself — draggable horizontally. */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -REVEAL_WIDTH, right: 0 }}
        dragElastic={{ left: 0.08, right: 0 }}
        animate={controls}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: "grabbing" }}
        className="relative z-10 touch-pan-y"
      >
        <button
          type="button"
          onClick={handleRowClick}
          disabled={!clickable && !revealed}
          data-testid={`alert-${alert.id}`}
          className={`w-full text-left tup-card p-4 flex items-start gap-3 border-l-4 ${style.ring} ${
            clickable || revealed ? "hover:bg-[#F5F3ED] transition-colors" : "cursor-default"
          }`}
        >
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.chip}`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm text-[#2D3142] truncate">{alert.title}</div>
            <div className="text-xs text-[#4F5D75] mt-0.5 leading-relaxed">{alert.body}</div>
          </div>
        </button>
      </motion.div>
    </li>
  );
};

/**
 * Wraps a list of alerts in an AnimatePresence so dismiss animations work.
 */
export const SwipeableAlertList = ({ alerts, onOpenContainer, onDelete }) => (
  <ul className="space-y-2.5" data-testid="alerts-list">
    <AnimatePresence initial={false}>
      {alerts.map((a) => (
        <motion.div
          key={a.id}
          layout
          initial={{ opacity: 1, height: "auto" }}
          exit={{
            opacity: 0,
            height: 0,
            marginTop: 0,
            marginBottom: 0,
            transition: { duration: 0.22 },
          }}
        >
          <SwipeableAlert
            alert={a}
            clickable={Boolean(a.container_id)}
            onOpen={() => a.container_id && onOpenContainer?.(a.container_id)}
            onDelete={() => onDelete?.(a.id)}
          />
        </motion.div>
      ))}
    </AnimatePresence>
  </ul>
);

export default SwipeableAlertList;
